import React from 'react';
import { Link } from 'react-router-dom';
import { QrCodeIcon, MessageCircle, Shield, Zap, Globe, Users, BarChart3, Palette, Clock, Database } from 'lucide-react';

export default function Features() {
  const features = [
    {
      icon: <QrCodeIcon className="h-12 w-12 text-blue-600" />,
      title: "QR Code Generation",
      description: "Generate unique QR codes instantly. Bulk creation available for premium users with CSV export functionality."
    },
    {
      icon: <MessageCircle className="h-12 w-12 text-green-600" />,
      title: "Anonymous Messaging",
      description: "Send and receive messages without revealing personal contact information. Perfect for privacy-focused communication."
    },
    {
      icon: <Shield className="h-12 w-12 text-purple-600" />,
      title: "Complete Privacy",
      description: "Your personal information stays private. Advanced security features for premium users including data encryption."
    },
    {
      icon: <Zap className="h-12 w-12 text-yellow-600" />,
      title: "Instant Notifications",
      description: "Get real-time email and SMS notifications. Premium users get priority delivery and custom notification settings."
    },
    {
      icon: <Globe className="h-12 w-12 text-indigo-600" />,
      title: "Global Access",
      description: "Works anywhere in the world. Premium users can use custom domains and white-label solutions."
    },
    {
      icon: <Users className="h-12 w-12 text-red-600" />,
      title: "Team Collaboration",
      description: "Share QR codes and manage messages as a team. Premium users get multi-user access and role management."
    },
    {
      icon: <BarChart3 className="h-12 w-12 text-orange-600" />,
      title: "Advanced Analytics",
      description: "Track message patterns, response rates, and QR code performance. Premium users get detailed reports and insights."
    },
    {
      icon: <Palette className="h-12 w-12 text-pink-600" />,
      title: "Custom Branding",
      description: "Customize QR code designs, colors, and add your logo. Premium users get white-label options and custom domains."
    },
    {
      icon: <Clock className="h-12 w-12 text-teal-600" />,
      title: "Auto-Response Templates",
      description: "Set up automatic response templates for common inquiries. Premium users get advanced template management and customization."
    },
    {
      icon: <Database className="h-12 w-12 text-gray-600" />,
      title: "Data Management",
      description: "Export your data and manage message history. Premium users get advanced data export capabilities and backup features."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-gray-900">Home</Link>
              <Link to="/auth" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Sign In</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Powerful Features for
            <span className="block text-yellow-300">Anonymous Communication</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Discover all the ways iComment.app makes private communication simple, secure, and effective.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="mb-6">{feature.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8">Join thousands of users who trust iComment.app for private communication.</p>
          <Link 
            to="/auth" 
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Create Your Account
          </Link>
        </div>
      </section>
    </div>
  );
}